"use client";

import {
  SaveType,
  type CollaboratorsOnLists,
  type FollowersOnLists,
  type GoogleImage,
  type List,
  type Place,
  type PlaceAffiliate,
  type Spot,
  type SpotImage,
  type Trip,
  type TripDay,
  type TripItem,
  type User,
  type UserSpotVisit,
} from "@prisma/client";
import Link from "next/link";
import Image from "next/image";
import React from "react";
import ExternalLinkIcon from "@/components/external-link-icon";
import UserIcon from "@/components/user-icon";
import Utils from "@/lib/utils";
import localizedFormat from "dayjs/plugin/localizedFormat";
import dayjs from "dayjs";
import LinkOrDiv from "../LinkOrDiv";
import type { Route } from "next";
import SaveButton from "../SaveButton";

dayjs.extend(localizedFormat);

export default function SpotView<R extends string>({
  spot,
  linkUrl,
  linkTarget,
  showListTitle,
  currentUser,
  googleMapsLink,
  fullMode,
}: {
  spot: Spot & {
    images: SpotImage[];
    place: Place & { googleImages: GoogleImage[]; affiliates: PlaceAffiliate[] };
    list: List & { user: User; spots: Spot[] };
    user: User;
  };
  linkUrl?: Route<R>;
  linkTarget?: "_blank";
  showListTitle?: boolean;
  currentUser:
    | (User & {
        lists: (List & { spots: Spot[] })[];
        followingLists: FollowersOnLists[];
        collaboratingLists: CollaboratorsOnLists[];
        visitedSpots: UserSpotVisit[];
        trips: (Trip & {
          tripDays: (TripDay & {
            items: (TripItem & {
              spot: Spot | null;
            })[];
          })[];
        })[];
      })
    | null;
  googleMapsLink?: boolean;
  fullMode?: boolean;
}) {
  let imageUrls: string[] = spot.images.map((image) => image.cachedUrl);

  if (spot.place.imagesCachedAt) {
    imageUrls = imageUrls.concat(spot.place.googleImages.map((i) => i.cachedUrl));
  }

  if (imageUrls.length === 0) {
    imageUrls.push("/images/spot-default.jpg");
  }

  const time = dayjs(spot.updatedAt).format("ll");

  return (
    <div className="relative rounded-lg border border-white bg-white md:h-[393px] md:w-full md:min-w-[353px] md:overflow-hidden">
      <LinkOrDiv href={linkUrl} link={!!linkUrl}>
        <div className="relative h-96 md:h-[251px]">
          <Image className="rounded-t-lg object-cover" src={imageUrls[0]} alt="" fill />
          {!fullMode && (
            <Link
              className="absolute bottom-4 left-4 text-xs font-medium text-white"
              href={`/v1/users/${spot.user.id}`}
              prefetch={false}
            >
              <UserIcon user={spot.user} showDisplayName={true} size={5} link={false} />
            </Link>
          )}
          {spot.place.locationImageUrl && (
            <div className="absolute bottom-4 right-4">
              <>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${spot.place.name}&query_place_id=${spot.place.googlePlaceId}`}
                  target="_blank"
                >
                  <div className="flex flex-col items-end gap-y-2.5">
                    <Image
                      className="overflow-hidden rounded-lg border"
                      src={spot.place.locationImageUrl}
                      alt={`${spot.place.name} location image}`}
                      width={66}
                      height={66}
                    />
                    <div className="rounded bg-white p-1.5 text-xs font-medium">
                      {spot.place.addressComponents &&
                        Utils.shortAddressFromGoogleAddressComponents(spot.place.addressComponents)}
                    </div>
                  </div>
                </a>
              </>
            </div>
          )}
        </div>
      </LinkOrDiv>

      <div className="absolute right-4 top-4 flex items-center justify-center rounded-full">
        <SaveButton type={SaveType.SPOT} id={spot.id} subId={spot.placeId} />
      </div>

      {googleMapsLink && (
        <div className="mt-2 px-2 text-left text-sm">
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${spot.place.name}&query_place_id=${spot.place.googlePlaceId}`}
            target="_blank"
          >
            <div className="flex">
              Find on Google Map <ExternalLinkIcon className="h-5 w-5" />
            </div>
          </a>
        </div>
      )}

      <div className="p-4">
        <div className="text-sm font-medium">
          {spot.name}

          {spot.id === "d00e5e87-2ba7-48a4-bb20-5927b599b6bf" && (
            <a href="https://wa.me/817064884342" target="_blank">
              <Image className="float-right" alt="whatsapp" src="/img/whatsapp.png" width={18} height={18} />
            </a>
          )}
        </div>

        {/*           <div className="mt-1 flex items-center gap-1 text-xs">
            <KidStarIcon />
            <div className="font-semibold text-variable-collection-primary-500">4.7</div>
            <div className="opacity-50">(39) on Ikura</div>
          </div> */}
        {fullMode ? (
          <>
            <div className="mt-2.5 border-t-[0.25px] border-[#00000080] py-3">
              <p className="text-sm md:line-clamp-2">{spot.comment}</p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="opacity-50">{time}</div>
              <div className="font-medium">
                <UserIcon user={spot.user} showDisplayName={true} size={5} link={false} />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mt-3 line-clamp-2 text-xs opacity-50">{spot.comment}</div>

            {showListTitle && (
              <div className="mt-1 text-xs">
                <span className="opacity-50">From </span>
                <Link href={`/guides/${spot.list.slug}`} className="underline">
                  {spot.list.name}
                </Link>{" "}
                <span className="opacity-50">({spot.list.spots.length} spots)</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
